import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import type { FieldValues } from "react-hook-form";
import { useComputedRedirectUri } from "@/app/oauth/api/get-callback-url-template";
import type { FieldProps, Server } from "@/app/servers/types";
import { ServerRedirectUriField } from "./redirect-uri-field";

type OAuth2FieldsProps = FieldProps & {
  server?: Server;
};

export const OAuth2Fields = ({
  form,
  canUpdate,
  server,
}: OAuth2FieldsProps) => {
  const [showSecret, setShowSecret] = useState(false);
  const isEditing = !!server;

  const externalId = form.getValues("mcp.oauth2.external_id") ?? "";
  const { redirectUri: computedRedirectUri, isLoading: configLoading } =
    useComputedRedirectUri(isEditing ? "" : externalId);

  // When editing, use the existing redirect URI from the server.
  // The engine returns redirect_uri at the top-level mcp config (always)
  // and inside oauth2 (only when credentials exist).
  const serverRedirectUri =
    server?.type === "mcp"
      ? (server.mcp?.redirect_uri ?? server.mcp?.oauth2?.redirect_uri)
      : undefined;
  const redirectUri =
    isEditing && serverRedirectUri ? serverRedirectUri : computedRedirectUri;

  const clientSecretExists =
    server?.type === "mcp" &&
    !!server.mcp?.oauth2?.client_secret &&
    typeof server.mcp.oauth2.client_secret === "object"
      ? server.mcp.oauth2.client_secret.exists
      : false;

  const getClientSecretPlaceholder = () => {
    if (!isEditing) {
      return "your-client-secret";
    }
    if (!canUpdate) {
      return clientSecretExists
        ? "Client secret is configured and cannot be modified"
        : "Client secret cannot be modified";
    }
    if (clientSecretExists) {
      return "Leave empty to keep current, or enter new secret to replace";
    }
    return "your-client-secret";
  };

  const getClientSecretDescription = () => {
    if (!(isEditing && clientSecretExists)) {
      return "Enter the OAuth2 client secret from your MCP server. This is for client authentication and is not the same as an API bearer token.";
    }
    if (!canUpdate) {
      return "A client secret is already configured for this server and cannot be changed.";
    }
    return "Leave this empty to keep the existing client secret, or enter a new value to replace it.";
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <FormLabel className="font-medium text-base">
            OAuth2 Authorization
          </FormLabel>
          <span className="text-muted-foreground text-xs">(Optional)</span>
        </div>
        <p className="text-muted-foreground text-sm">
          Configure OAuth2 credentials if your MCP server requires
          authentication
        </p>
      </div>
      <FormField
        control={form.control}
        name="mcp.oauth2.client_id"
        render={({ field }: { field: FieldValues }) => (
          <FormItem>
            <FormLabel>Client ID</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!canUpdate}
                placeholder="your-client-id"
                type="text"
              />
            </FormControl>
            <FormDescription>
              The OAuth2 client ID provided by your MCP server
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="mcp.oauth2.client_secret"
        render={({ field }: { field: FieldValues }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <span>Client Secret</span>
              {!!(isEditing && clientSecretExists && canUpdate) && (
                <Badge className="text-xs" variant="outline">
                  Optional - Leave empty to keep current
                </Badge>
              )}
            </FormLabel>
            {!!(isEditing && clientSecretExists) && (
              <div className="flex items-center justify-between rounded border-2 border-amber-200/60 bg-amber-50/30 p-2 dark:border-amber-800/40 dark:bg-amber-950/20">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  <span className="font-medium text-xs">A secret is set</span>
                </div>
              </div>
            )}
            <FormControl>
              <div className="relative">
                <Input
                  {...field}
                  className="pr-10"
                  disabled={!canUpdate}
                  placeholder={getClientSecretPlaceholder()}
                  type={showSecret ? "text" : "password"}
                />
                <Button
                  className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground"
                  onClick={() => setShowSecret(!showSecret)}
                  size="sm"
                  type="button"
                  variant="link"
                >
                  {showSecret ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </Button>
              </div>
            </FormControl>
            <FormDescription>{getClientSecretDescription()}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="mcp.oauth2.authorization_url"
        render={({ field }: { field: FieldValues }) => (
          <FormItem>
            <FormLabel>Authorization URL</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!canUpdate}
                placeholder="Leave empty if same as MCP URI, which is most common"
                type="url"
              />
            </FormControl>
            <FormDescription>
              Only required if the authorization endpoint is different from your
              MCP server URI
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      {!!(redirectUri || isEditing) && (
        <ServerRedirectUriField
          isLoading={configLoading}
          redirectUri={redirectUri}
        />
      )}
    </div>
  );
};
